'use client';

/**
 * 📂 File: src/app/student-animation-showcase/StudentAnimationShowcaseClient.tsx
 * 🧭 Description:
 * Clean 2026 SAS page using timeline-based rendering (no phase toggles)
 */

import type { StudentAnimationShowcasePageQueryResponse } from './types';

import { formatPrettyDate, parseDate } from './helpers/dates';

import { getEventByYear } from './helpers/event';
// import dynamic from 'next/dynamic';
import { PortableText } from '@portabletext/react';

// Components
import HeroHeading from '@components/ui/HeroHeading';
import Logo from '@components/images/Logo';
import LayoutContainer from '@components/ui/LayoutContainer';
import ImageContainer from '@components/images/ImageContainer';
import ContentBlock from '@components/blocks/ContentBlock';
import Button from '@components/buttons/BaseButton';

// Styles
import { ListInline, ListItem, RedBoldText, StyledSection } from './styles';
// import styled from 'styled-components';

// const ZoomButton = styled(Button)`
//   &&,
//   &&:hover,
//   &&:focus,
//   &&:active,
//   &&:visited {
//     background: #2d8cff;
//     border-color: #2d8cff;
//     color: #fff !important;
//   }
//
//   &:hover {
//     background: #2681f2;
//     border-color: #2681f2;
//   }
// `;

// ✅ Countdown (client only)
// const CountdownTimer = dynamic(
//   () => import('../../components/CountdownTimer'),
//   { ssr: false },
// );

/* ---------------- COMPONENT ---------------- */

export interface StudentAnimationShowcaseClientProps {
  data: StudentAnimationShowcasePageQueryResponse;
}

export default function StudentAnimationShowcaseClient({
  data,
}: StudentAnimationShowcaseClientProps) {
  const {
    headingOne,
    subheading,
    pageImageBlock,
    sasPremierBlocks = [],
    sasTextBlock,
  } = data.sasPageData[0];

  /* -------------------------------------------------- */
  /* 🎬 Showcase Configuration                          */
  /* -------------------------------------------------- */

  const SHOWCASE_YEAR = 2026;

  const showcase = getEventByYear(sasPremierBlocks, SHOWCASE_YEAR);
  /* ---------------- CLIENT READY ---------------- */
  // const [mounted, setMounted] = useState(false);

  // useEffect(() => {
  //   setMounted(true);
  // }, []);

  /* ---------------- DATES ---------------- */

  const now = new Date();

  const submissionGoLiveDate = parseDate(showcase?.submissionFormGoLiveDate);

  const deadlineDate = parseDate(showcase?.deadlineSubmissionFormDate);

  // const premierDate = parseDate(
  //   showcase?.premierDate,
  //   showcase?.premierTime,
  //   showcase?.timezone,
  // );
  const showSubmissionButton =
    !!submissionGoLiveDate &&
    !!deadlineDate &&
    now >= submissionGoLiveDate &&
    now <= deadlineDate;

  /* ---------------- VISIBILITY ---------------- */

  const showSubmissionSection =
    !!submissionGoLiveDate && !!deadlineDate && now <= deadlineDate;

  const isBeforeOpen = submissionGoLiveDate && now < submissionGoLiveDate;

  const showDeadline = !!deadlineDate && now.getTime() < deadlineDate.getTime();

  // const joinButtonVisible =
  //   !!premierDate && now.getTime() >= premierDate.getTime() - 60 * 60 * 1000;

  /* ---------------- UI ---------------- */

  const timelineBlock = (
    <ContentBlock>
      <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
        {/* <h2>Announcing The 2026 Student Animation Showcase</h2> */}

        {/* ---------------- SUBMISSION GO LIVE ---------------- */}
        {showSubmissionSection && (
          <div style={{ marginTop: '2rem' }}>
            <h3>
              {isBeforeOpen
                ? 'Submission Form Goes Live'
                : 'Submissions Are Now Open'}
            </h3>

            <p style={{ fontSize: '1.5rem' }}>
              <RedBoldText>
                {formatPrettyDate(submissionGoLiveDate)}
              </RedBoldText>
            </p>

            {/* CTA: Submit */}
            {showSubmissionButton && showcase?.submissionFormLink && (
              <div style={{ marginTop: '1.5rem' }}>
                <Button
                  theme="solid"
                  actionType="external"
                  link={showcase.submissionFormLink}
                >
                  2026 Student Animation Showcase Submission Form
                </Button>
              </div>
            )}

            {/* CTA: Permission PDF */}
            {/* {showcase?.studentPermissionForm?.asset?.url && ( */}
            {/*   <div style={{ marginTop: '1rem' }}> */}
            {/*     <Button */}
            {/*       theme="outline" */}
            {/*       actionType="external" */}
            {/*       link={showcase.studentPermissionForm.asset.url} */}
            {/*     > */}
            {/*       {showcase?.studentPermissionLabel || */}
            {/*         'Download Student Permission Form'} */}
            {/*     </Button> */}
            {/*   </div> */}
            {/* )} */}
          </div>
        )}

        {/* ---------------- DEADLINE ---------------- */}
        {showDeadline && (
          <div style={{ marginTop: '2rem' }}>
            <h3>Deadline for Submissions</h3>

            <p style={{ fontSize: '1.5rem' }}>
              <RedBoldText>{formatPrettyDate(deadlineDate)}</RedBoldText>
            </p>
          </div>
        )}

        {/* ---------------- Archive Block ---------------- */}
        <div
          style={{
            marginTop: '3rem',
            padding: '2rem',
            border: '2px solid #F9BB1B',
            borderRadius: '12px',
          }}
        >
          <h2>🎉 The {SHOWCASE_YEAR} Student Animation Showcase</h2>

          <p style={{ marginTop: '1rem', fontSize: '1.2rem' }}>
            Thank you to everyone who participated in this year&apos;s showcase.
          </p>

          <p style={{ marginTop: '1rem' }}>
            <strong>{showcase?.numberOfSchools}</strong> participating schools
            <br />
            <strong>{showcase?.numberOfFilms}</strong> student animations
          </p>

          {/* ---------------- Directors Choice Winners ---------------- */}
          <div
            style={{
              marginTop: '3rem',
              padding: '2rem',
              background: '#fff8e5',
              borderRadius: '12px',
            }}
          >
            <h2>🎉 Directors&apos; Choice Top Five Films</h2>

            <div
              style={{
                marginTop: '2rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '1.5rem',
              }}
            >
              {[
                {
                  title: 'The Clock that Forgot to Chime',
                  director: 'Vanessa Cunha',
                  school: 'Drexel University',
                },
                {
                  title: "I'll See You in My Dreams",
                  director: 'Natalie Perez',
                  school: 'Arizona State University',
                },
                {
                  title: 'Alien Vacation',
                  director: 'Arina Glozman',
                  school: 'Drexel University',
                },
                {
                  title: 'This Dangerous Yet Beautiful World',
                  director: 'Maxwell Smith',
                  school: 'Parsons School of Design',
                },
                {
                  title: 'Struggle Bug',
                  director: 'Maya Murman',
                  school: 'Ringling College of Art and Design',
                },
              ].map((film) => (
                <div key={film.title}>
                  <h3 style={{ marginBottom: '.25rem' }}>
                    <em>{film.title}</em>
                  </h3>

                  <p>
                    <strong>Director:</strong> {film.director}
                    <br />
                    {film.school}
                  </p>
                </div>
              ))}
            </div>

            <p
              style={{
                marginTop: '2rem',
                fontSize: '1.3rem',
                fontWeight: 700,
              }}
            >
              Congratulations to the Directors&apos; Top Five of {SHOWCASE_YEAR}
              !
            </p>
          </div>

          <div
            style={{
              marginTop: '2rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              alignItems: 'center',
            }}
          >
            <Button
              theme="solid"
              actionType="external"
              link="https://studentanimationshowcase.com"
            >
              View the {SHOWCASE_YEAR} Student Animation Showcase
            </Button>

            {showcase?.premierRecordedEventLink && (
              <Button
                theme="outline"
                actionType="external"
                link={showcase.premierRecordedEventLink}
              >
                Watch the Premiere Recording
              </Button>
            )}
          </div>
        </div>

        {/* ---------------- PREVIOUS SCHOOLS ---------------- */}
        {/* {previousEvent?.schools?.length ? ( */}
        {/*   <div style={{ marginTop: '3rem' }}> */}
        {/*     <h3>Last Year’s Participating Schools</h3> */}
        {/**/}
        {/*     <ListInline> */}
        {/*       {previousEvent.schools.map((school, i) => ( */}
        {/*         <ListItem key={i}>{school}</ListItem> */}
        {/*       ))} */}
        {/*     </ListInline> */}
        {/*   </div> */}
        {/* ) : null} */}

        {/* ---------------- PARTICIPATING SCHOOLS ---------------- */}
        {showcase?.schools?.length ? (
          <div style={{ marginTop: '3rem' }}>
            <h3>2026 Participating Schools</h3>

            <ListInline>
              {showcase.schools.map((school, i) => (
                <ListItem key={i}>{school}</ListItem>
              ))}
            </ListInline>
          </div>
        ) : null}
      </div>
    </ContentBlock>
  );

  /* ---------------- RENDER ---------------- */

  return (
    <>
      <HeroHeading>
        <Logo />
        <h1>{headingOne}</h1>
        <p>{subheading}</p>

        {pageImageBlock && (
          <ImageContainer
            image={{
              url: pageImageBlock.asset.url,
              alt: pageImageBlock.alt || '',
              asset: pageImageBlock.asset,
              crop: pageImageBlock.crop as any,
              hotspot: pageImageBlock.hotspot as any,
            }}
            width={751}
            height={402}
            type="dynamic"
            alt={pageImageBlock.alt || ''}
          />
        )}
      </HeroHeading>

      <LayoutContainer hasSectionGaps>
        <StyledSection>
          {timelineBlock}

          <StyledSection>
            <ContentBlock>
              <PortableText value={sasTextBlock as any} />
            </ContentBlock>
          </StyledSection>
        </StyledSection>
      </LayoutContainer>
    </>
  );
}
